import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";
import uuid from "react-uuid";
import api from "src/http/index";
import { ErrorResponse } from "src/types/error-response.type";
import { SuccessResponse } from "src/types/success-response.type";

type UserType = {
    id: number;
    login: string;
    first_name: string;
    last_name: string;
}

type LoginPassword = {
    login: string,
    password: string
}

type UserState = {
    accessToken: string | null,
    refreshToken: string | null,
    user: UserType | null,
    loading: boolean;
    error: string | null;
}

const initialState: UserState = {
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    user: null,
    loading: false,
    error: "",
}

type AuthResponse = {
    accessToken: string;
    refreshToken: string;
    user: UserType;
}

export const doLogin = createAsyncThunk<AuthResponse, LoginPassword>(
    'user/doLogin',
    async (loginPassword, { rejectWithValue, fulfillWithValue }) => {
        var fingerprint = localStorage.getItem('fingerprint');

        if (!fingerprint) {
            fingerprint = uuid();
            localStorage.setItem("fingerprint", fingerprint);
        }

        localStorage.setItem("last_login", loginPassword.login);

        try {
            const response = await api.post<AuthResponse>('/auth/login',
                { login: loginPassword.login, password: loginPassword.password, fingerprint });

            if (response.status !== 200) {
                return rejectWithValue('Request ERROR');
            }
            return fulfillWithValue(response.data);
        } catch (e) {
            if (isAxiosError(e)) {
                const err = e as AxiosError;
                return rejectWithValue(err.response?.data as ErrorResponse);
            }
            return rejectWithValue("Request ERROR");
        }


    })

export const doLogout = createAsyncThunk<SuccessResponse, void>(
    'user/doLogout',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const response = await api.post<SuccessResponse>('/auth/logout');

            if (response.status! in [200, 201]) {
                return rejectWithValue('Request ERROR');
            }
            return fulfillWithValue(response.data);
        } catch (e) {
            if (isAxiosError(e)) {
                const err = e as AxiosError;
                return rejectWithValue(err.response?.data as ErrorResponse);
            }
            return rejectWithValue("Request ERROR");
        }


    })

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserState(state, action: PayloadAction<AuthResponse>) {
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            const accessToken = action.payload.accessToken ? action.payload.accessToken : "";
            const refreshToken = action.payload.refreshToken ? action.payload.refreshToken : "";
            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            state.user = action.payload.user;
        },
        clearUserState(state) {
            state.accessToken = null;
            state.refreshToken = null;
            state.user = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(doLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(doLogin.fulfilled, (state, action) => {
                state.loading = false;
                userSlice.caseReducers.setUserState(state, action);
            })
            .addCase(doLogin.rejected, (state, action) => {
                state.loading = false;
                // console.log(action.payload);
                const errorText = getErrorText(action.payload as ErrorResponse);
                state.error = errorText ? errorText : "";
            })
            .addCase(doLogout.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(doLogout.fulfilled, (state, _action) => {
                state.loading = false;
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');

                let stateNew = state;
                stateNew.accessToken = null;
                stateNew.refreshToken = null;
                stateNew.user = null;
                state = stateNew;

            })
            .addCase(doLogout.rejected, (state, action) => {
                state.loading = false;
                // console.log(action.payload);
                state.error = "";
            })
    }
})

const getErrorText = (error: ErrorResponse) => {
    if (error) {
        switch (error.error) {
            case "invalid_password":
                return "Неверный пароль";
            case "user_not_found":
                return "Пользователь не найден";
            default:
                return "";
        }
    }

    return "";
}

export const { setUserState, clearUserState } = userSlice.actions;
export type { LoginPassword };

export const userReducer = userSlice.reducer;